import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMethod, postMethod } from "../../services/ApiCallMethods";

export const getReportFinancialYears = createAsyncThunk(
  "sendReport/getReportFinancialYears",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/reports/financial-years`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch financial years");
    }
  }
);

// Unified list — property_master and asset_group entries together, each tagged with `type` so
// the frontend can split a selection back into propertyMasterIds/assetGroupIds on submit.
// Scoped server-side to only investments with a report matching this financialYear/quarter —
// so nothing shown here can produce a "no report for this period" surprise after sending.
export const getReportInvestments = createAsyncThunk(
  "sendReport/getReportInvestments",
  async ({ financialYear, quarter }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (financialYear) params.append("financialYear", financialYear);
      if (quarter) params.append("quarter", quarter);
      const response = await getMethod(`/api/reports/investments?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch investments");
    }
  }
);

export const getInvestmentAccounts = createAsyncThunk(
  "sendReport/getInvestmentAccounts",
  async ({ financialYear, quarter, propertyMasterIds, assetGroupIds }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (financialYear) params.append("financialYear", financialYear);
      if (quarter) params.append("quarter", quarter);
      if (propertyMasterIds?.length) params.append("propertyMasterIds", propertyMasterIds.join(","));
      if (assetGroupIds?.length) params.append("assetGroupIds", assetGroupIds.join(","));
      const response = await getMethod(`/api/reports/investment-accounts?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch investment accounts");
    }
  }
);

export const getInvestmentReportViewUrl = createAsyncThunk(
  "sendReport/getInvestmentReportViewUrl",
  async ({ financialYear, quarter, id, type }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ financialYear, quarter, id, type });
      const response = await getMethod(`/api/reports/investment-view-url?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data.data;
    } catch (error) {
      return rejectWithValue({ message: error?.message || "Failed to retrieve the report link" });
    }
  }
);

export const sendReportEmails = createAsyncThunk(
  "sendReport/sendReportEmails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/api/reports/send`, payload);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ status: "FAILURE", message: error?.message || "Failed to send reports" });
    }
  }
);

const initialState = {
  financialYears: [],
  investments: [],
  investmentAccounts: [],
  loading: false,
  investmentsLoading: false,
  investmentAccountsLoading: false,
  sending: false,
};

const sendReportSlice = createSlice({
  name: "sendReport",
  initialState,
  reducers: {
    clearInvestmentAccounts: (state) => {
      state.investmentAccounts = [];
    },
    clearInvestments: (state) => {
      state.investments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReportFinancialYears.fulfilled, (state, action) => {
        state.financialYears = action.payload || [];
      })
      .addCase(getReportInvestments.pending, (state) => {
        state.investmentsLoading = true;
      })
      .addCase(getReportInvestments.fulfilled, (state, action) => {
        state.investmentsLoading = false;
        state.investments = action.payload || [];
      })
      .addCase(getReportInvestments.rejected, (state) => {
        state.investmentsLoading = false;
      })
      .addCase(getInvestmentAccounts.pending, (state) => {
        state.investmentAccountsLoading = true;
      })
      .addCase(getInvestmentAccounts.fulfilled, (state, action) => {
        state.investmentAccountsLoading = false;
        state.investmentAccounts = action.payload || [];
      })
      .addCase(getInvestmentAccounts.rejected, (state) => {
        state.investmentAccountsLoading = false;
      })
      .addCase(sendReportEmails.pending, (state) => {
        state.sending = true;
      })
      .addCase(sendReportEmails.fulfilled, (state) => {
        state.sending = false;
      })
      .addCase(sendReportEmails.rejected, (state) => {
        state.sending = false;
      });
  },
});

export const { clearInvestmentAccounts, clearInvestments } = sendReportSlice.actions;
export default sendReportSlice.reducer;
